import type { IMappingValidationResult, ISpreadsheetTemplateActive, IWasteReturnTemplateMapping } from "../../customTypings/spreadsheetTemplate/SpreadsheetTemplate";
import { countExtractedValuesInMapping } from "./regDataAlignment/regDataAlignmentDisplay";

export type WasteReturnImportStep = "upload" | "map" | "sync" | "save";

export const WASTE_RETURN_IMPORT_STEPS: WasteReturnImportStep[] = ["upload", "map", "sync", "save"];

export const WASTE_RETURN_IMPORT_STEP_LABELS: Record<WasteReturnImportStep, string> = {
    upload: "Upload",
    map: "Map fields",
    sync: "Synchronise",
    save: "Validate",
};

export function parseImportStep(value: string | null): WasteReturnImportStep | null {
    if (!value) return null;
    return WASTE_RETURN_IMPORT_STEPS.includes(value as WasteReturnImportStep) ? (value as WasteReturnImportStep) : null;
}

export function getMaxReachableImportStep(params: {
    hasActiveTemplate: boolean;
    hasMapping: boolean;
    mapping: IWasteReturnTemplateMapping;
    validation: IMappingValidationResult | null | undefined;
}): WasteReturnImportStep {
    if (!params.hasActiveTemplate) return "upload";
    if (!params.hasMapping) return "map";
    if (countExtractedValuesInMapping(params.mapping.listSources) <= 0) return "map";
    if (!params.validation?.isValid) return "sync";
    return "save";
}

/** Furthest step whose completion criteria are met — used only to seed the track on load/reload. */
export function getFurthestCompletedImportStep(params: {
    hasActiveTemplate: boolean;
    hasMapping: boolean;
    mapping: IWasteReturnTemplateMapping;
    validation: IMappingValidationResult | null | undefined;
}): WasteReturnImportStep {
    if (!params.hasActiveTemplate) return "upload";
    if (!params.hasMapping) return "upload";
    if (countExtractedValuesInMapping(params.mapping.listSources) <= 0) return "map";
    if (!params.validation?.isValid) return "map";
    return "save";
}

/** Grow furthest-visited monotonically when the user navigates forward; never retract on back-nav. */
export function advanceFurthestVisitedOnNavigation(
    previous: WasteReturnImportStep,
    current: WasteReturnImportStep,
    maxReachable: WasteReturnImportStep
): WasteReturnImportStep {
    const previousIndex = WASTE_RETURN_IMPORT_STEPS.indexOf(previous);
    const currentIndex = WASTE_RETURN_IMPORT_STEPS.indexOf(current);
    const maxIndex = WASTE_RETURN_IMPORT_STEPS.indexOf(maxReachable);
    if (currentIndex <= previousIndex) {
        return previous;
    }
    const nextIndex = Math.min(currentIndex, maxIndex);
    return WASTE_RETURN_IMPORT_STEPS[nextIndex];
}

export function clampImportStep(requested: WasteReturnImportStep | null, maxReachable: WasteReturnImportStep): WasteReturnImportStep {
    if (!requested) return maxReachable;
    const requestedIndex = WASTE_RETURN_IMPORT_STEPS.indexOf(requested);
    const maxIndex = WASTE_RETURN_IMPORT_STEPS.indexOf(maxReachable);
    if (requestedIndex <= maxIndex) return requested;
    return maxReachable;
}

export function isStepReachable(step: WasteReturnImportStep, maxReachable: WasteReturnImportStep): boolean {
    return WASTE_RETURN_IMPORT_STEPS.indexOf(step) <= WASTE_RETURN_IMPORT_STEPS.indexOf(maxReachable);
}

export function getImportStepFromActiveTemplate(active: ISpreadsheetTemplateActive | null): WasteReturnImportStep {
    if (!active) return "upload";
    if (!active.hasMapping) return "map";
    return "sync";
}
